import { ENTITY_TO_ROUTE_MAP } from "../config/trackingConfig";
import {
  SIDEBAR_ITEMS,
  SidebarItem,
  SidebarEntry,
} from "../config/sidebarConfig";
import { SvgIconComponent } from "@mui/icons-material";

// Icons
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import InventoryIcon from "@mui/icons-material/Inventory";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BusinessIcon from "@mui/icons-material/Business";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import SmsIcon from "@mui/icons-material/Sms";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import CampaignIcon from "@mui/icons-material/Campaign";
import FolderIcon from "@mui/icons-material/Folder";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PaymentsIcon from "@mui/icons-material/Payments";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import RedeemIcon from "@mui/icons-material/Redeem";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BookIcon from "@mui/icons-material/Book";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import NotificationsIcon from "@mui/icons-material/Notifications";

export interface EntityMeta {
  icon: SvgIconComponent;
  color: string;
  title: string;
}

export const ENTITY_META: Record<string, EntityMeta> = {
  SALE: { icon: PointOfSaleIcon, color: "#2e7d32", title: "Sales & POS" }, // Green
  ITEM: { icon: InventoryIcon, color: "#1976d2", title: "Inventory Items" }, // Blue
  CUSTOMER: { icon: PeopleIcon, color: "#9c27b0", title: "Customers" }, // Purple
  SHIFT: { icon: AccessTimeIcon, color: "#ed6c02", title: "Shifts" }, // Orange
  EXPENSE: { icon: CreditCardIcon, color: "#d32f2f", title: "Expenses" }, // Red
  USER: {
    icon: AdminPanelSettingsIcon,
    color: "#607d8b",
    title: "System Users",
  }, // Blue Grey
  BRANCH: { icon: BusinessIcon, color: "#00796b", title: "Branches" }, // Teal
  SUPPLIER: { icon: LocalShippingIcon, color: "#f57c00", title: "Suppliers" }, // Dark Orange
  AUDIT_LOG: { icon: ManageHistoryIcon, color: "#455a64", title: "Audit Logs" },
  SMS_OUTBOX: { icon: SmsIcon, color: "#3f51b5", title: "SMS Outbox" }, // Indigo
  SMS_TEMPLATE: {
    icon: TextSnippetIcon,
    color: "#795548",
    title: "SMS Templates",
  }, // Brown
  ANNOUNCEMENT: {
    icon: CampaignIcon,
    color: "#e91e63",
    title: "Announcements",
  }, // Pink
  RECEIPT: { icon: ReceiptIcon, color: "#00bcd4", title: "Receipts Today" },
  INVOICE: { icon: ReceiptLongIcon, color: "#ffb300", title: "Invoices Today" },
  DEBT: { icon: MoneyOffIcon, color: "#f44336", title: "Debts Today" },
  PAYMENT: { icon: PaymentsIcon, color: "#4caf50", title: "Payments Today" },
  PURCHASE_ORDER: {
    icon: ShoppingCartIcon,
    color: "#8d6e63",
    title: "Purchase Orders Today",
  },
  CUSTOMER_ORDER: {
    icon: ShoppingBagIcon,
    color: "#673ab7",
    title: "Customer Orders Today",
  },
  CREDIT_NOTE: {
    icon: RedeemIcon,
    color: "#03a9f4",
    title: "Credit Notes Today",
  },
  QUOTATION: {
    icon: RequestQuoteIcon,
    color: "#ff5722",
    title: "Quotations Today",
  },
  ACCOUNT: {
    icon: AccountBalanceWalletIcon,
    color: "#3f51b5",
    title: "Accounts Overview",
  }, // Indigo
  LEDGER_ENTRY: {
    icon: BookIcon,
    color: "#009688",
    title: "Ledger Entries Today",
  }, // Teal
  INVENTORY_LOG: {
    icon: SyncAltIcon,
    color: "#607d8b",
    title: "Inventory Movements",
  }, // Blue Grey
  NOTIFICATION: {
    icon: NotificationsIcon,
    color: "#2196f3",
    title: "Notifications",
  }, // Blue
  DEFAULT: { icon: FolderIcon, color: "#757575", title: "Module" },
};

export const getEntityMeta = (entityKey: string): EntityMeta => {
  return ENTITY_META[entityKey] || { ...ENTITY_META.DEFAULT, title: entityKey };
};

const isSidebarItem = (entry: SidebarEntry): entry is SidebarItem => {
  return !("isHeader" in entry);
};

// Crucial helper for Workspace Tab integration
export const getSidebarItemByEntity = (
  entityKey: string,
): SidebarItem | null => {
  const route = ENTITY_TO_ROUTE_MAP[entityKey];
  if (!route) return null;
  return (
    SIDEBAR_ITEMS.filter(isSidebarItem).find((item) => item.path === route) ||
    null
  );
};
